import { useEffect } from "react"
import Cookies from "js-cookie"
import { useAuthStore } from "@/stores/auth"
import { useGetBusinessQuery } from "@/queries/business/business"
import { useActiveBusiness, useActiveBusinessId, useBusinesses } from "@/hooks/use-business"
import type { Business } from "@/types/business"
import { BusinessSidebar } from "@/components/business/sidebar"
import { BusinessHeader } from "@/components/business/header"
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar"
import { ScrollToBottomButton } from "@/components/scroll-to-bottom-button"
import { Toaster } from "@/components/ui/sonner"
import { ErrorBoundary } from "@/components/error-boundary"
import { NavigationProgress } from "@/components/navigation-progress"
import { CommandPaletteProvider } from "@/components/command-palette"
import { PageTransition } from "@/components/page-transition"
import { PremiumPaymentBanner } from "@/components/business/premium-payment-banner"
import { TourProvider, useTour } from "@/components/ui/tour"
import { tours, SYSTEM_TOUR_ID } from "@/config/tours"

function AppLayoutContent() {
  const { start } = useTour()

  const user = useAuthStore((state) => state.user)
  const activeBusiness = useActiveBusiness()
  const activeBusinessId = useActiveBusinessId()
  /** Merged tenant list (matches console switcher), not only `user.businesses` */
  const businesses = useBusinesses()

  const { data: businessData } = useGetBusinessQuery(activeBusinessId || "", {
    skip: !activeBusinessId,
  })

  const business: Business | undefined = businessData?.data
    ? (businessData.data as Business)
    : activeBusiness
      ? (activeBusiness as Business)
      : undefined

  useEffect(() => {
    if (businesses.length > 0) {
      const completed = localStorage.getItem("tour-completed-system")
      if (!completed) {
        const timer = setTimeout(() => start(SYSTEM_TOUR_ID), 1000)
        return () => clearTimeout(timer)
      }
    }
  }, [businesses.length, start])

  return (
    <>
      <NavigationProgress />
      <BusinessSidebar
        business={business}
        businesses={businesses}
        user={
          user
            ? {
                name: user.name ?? undefined,
                email: user.email ?? undefined,
              }
            : undefined
        }
        onStartTour={() => start(SYSTEM_TOUR_ID)}
      />
      <SidebarInset>
        <BusinessHeader />
        <PremiumPaymentBanner />
        <main className="flex-1 px-2 sm:px-4 py-4 md:py-6">
          <ErrorBoundary fallbackTitle="This page failed to load">
            <PageTransition />
          </ErrorBoundary>
        </main>
        <ScrollToBottomButton />
      </SidebarInset>
      <Toaster />
    </>
  )
}

export default function AppLayout() {
  const defaultOpen = Cookies.get("sidebar_state") !== "false"

  return (
    <TourProvider tours={tours}>
      <SidebarProvider defaultOpen={defaultOpen}>
        <CommandPaletteProvider scope="business">
          <AppLayoutContent />
        </CommandPaletteProvider>
      </SidebarProvider>
    </TourProvider>
  )
}
